
import { FC, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/react';

import { useAuthenticatedUser } from '@/hooks/data/useAuthenticatedUser';
import { AuthContext } from '@/context/AuthContext';
import IHasChildren from '@/types/IHasChildren';
import { AuthenticateResponse } from '@/api/dtos';
import { CircularProgress } from '@mui/material';
import Loading from '@/components/layout/Loading';
import AlertContext from '@/context/AlertContext';
import { createSentryAnonymousUser } from '@/errors/error';

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}


interface IAuthProviderProps extends IHasChildren {
    initialValue: AuthenticateResponse;
}


const AuthProvider: FC<IAuthProviderProps> = ({
    children,
    initialValue
}) => {
    const { addAlert } = useContext(AlertContext);
    const authenticatedUser = useAuthenticatedUser({ revalidateOnFocus: true, errorRetryCount: 0,  refreshInterval: 600000, fallbackData: initialValue });

    useEffect(() => {
        if (!authenticatedUser.data?.userName && !authenticatedUser.loading) { 
            global.location.href = `/auth/openiddict?redirect=${encodeURIComponent(global.location.pathname + global.location.search)}`;
        }
        else if (authenticatedUser.error) {
            addAlert({
                timeout: 8000,
                content: authenticatedUser.error.message,
                dismissable: true,
                severity: 'error'
            });
        }
    }, [authenticatedUser.data, authenticatedUser.error, authenticatedUser.loading])


    useEffect(() => {
        if (!authenticatedUser.data) {
            Sentry.setUser(createSentryAnonymousUser());
            return;
        }
        Sentry.setUser({
            id: authenticatedUser.data.userName,
            username: authenticatedUser.data.userName,
            fullName: authenticatedUser.data.displayName,
            authenticated: true
        });
        return () => {
            Sentry.setUser(createSentryAnonymousUser());
        };
    }, [authenticatedUser.data])

    return (
        <AuthContext.Provider value={authenticatedUser}>
            <Loading name="authenticatedUser" isLoading={authenticatedUser.loading} isValidating={authenticatedUser.validating} />
            {!authenticatedUser.loading && children}
        </AuthContext.Provider>
    )
}

export default AuthProvider